import { saveRecord, reloadRecord } from 'tio-data/requests/save-record';
import { setTrackedTasFieldValue } from 'tio-common/utils/tuition-assistance/fields';
import { buildTasEmployeeActor } from 'tio-data/utils/tas-actor';
import {
  buildTasTransitionFieldData,
  requestEvidenceApprovalRequest,
} from 'tio-data/requests/tas-application-actions';
import type { Store as StoreService } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasCourseExtension as TasCourseModel } from 'tio-data/data/resources/tas-course.ext';
import type { ConditionExtension as ConditionModel } from 'tio-data/data/resources/condition.ext';
import type { Agreement as AgreementModel } from 'tio-data/data/resources/agreement.type';
import type { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';
import type SessionContextService from 'tio-employee/services/session-context';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';

/**
 * Course-evidence submission mutations shared with (mirrors, does not modify)
 * the legacy show page's flow (`authenticated/tas/applications/show.gts`) —
 * grade entry, GPA, condition agreements, and the final approval-request
 * transition — so the "manage" screen's Submit Evidence task performs the
 * same real actions instead of just navigating into the guided wizard.
 */

/** Sets a course's final grade and saves it immediately (legacy: onSelectionChange). */
export async function saveCourseGrade(
  store: StoreService,
  course: TasCourseModel,
  grade: string
): Promise<void> {
  setTrackedTasFieldValue(course, 'COURSE_GRADE', grade);
  await saveRecord(store, course);
}

/**
 * Sets the application's GPA and the GPA-derived final approved total
 * (legacy: saveGPA). Only relevant when the template's `showGPAField`
 * equivalent gate is true.
 */
export async function saveApplicationGpa(
  store: StoreService,
  tuitionAssistance: TuitionAssistanceService,
  application: TasApplicationModel,
  gpa: number
): Promise<void> {
  const gpaBasedAmount = tuitionAssistance.setTotalBasedOnGPA(gpa);
  setTrackedTasFieldValue(application, 'GRADE_POINT_AVERAGE', gpa);
  setTrackedTasFieldValue(
    application,
    'FINAL_APPROVED_TOTAL',
    Math.min(gpaBasedAmount, application.requestedTotal)
  );
  await saveRecord(store, application);
}

/** Creates + saves one Agreement record per accepted condition (legacy: saveAgreementsForConditions). */
export async function saveAgreementsForConditions(
  store: StoreService,
  user: UserModel,
  acceptedConditions: ConditionModel[]
): Promise<void> {
  const agreements = acceptedConditions.map((condition) =>
    store.createRecord<AgreementModel>('agreement', { user, condition })
  );
  await Promise.all(agreements.map((agreement) => saveRecord(store, agreement)));
}

/**
 * Fires the real request-evidence-approval transition and reloads the
 * application (legacy: submitForEvidenceApproval, minus the router
 * navigation/error-state handling, which stays with the caller).
 */
export async function submitEvidenceForApproval(
  store: StoreService,
  sessionContext: SessionContextService,
  application: TasApplicationModel
): Promise<void> {
  await store.request(
    requestEvidenceApprovalRequest(
      application.id as string,
      buildTasEmployeeActor(sessionContext.user, sessionContext.currentRole),
      '',
      buildTasTransitionFieldData(application.fields)
    )
  );
  // request-evidence-approval goes through StateTransitionableController#transition
  // → `head 204` (no resource), so the builder does not refresh the cache —
  // reload explicitly to pick up the new state.
  await reloadRecord(store, application);
}
